import React, { useMemo } from 'react';
import { CatalogItemType } from '@common/model/catalog/item/type';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { DeclinationMnemonic } from '@common/model/resource/declinationMnemonic';
import { Typography } from '@cp/ds/src/components/typography';
import { Box } from '@cp/ds/src/components/box';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { CareerId } from '@common/const/catalog/career';
import { SxProps } from '@cp/ds';
import { styled } from '@cp/ds/src/theme/styled';
import { alpha } from '@mui/system';
import { ClientType } from '@common/model/client/clientType';
import { useCareerDeclination } from '@cp/entities/career';
import { coreCatalogApi } from '@cp/entities/catalog';
import { useDeclination } from '@cp/shared/declination';
import { clientTypeToSuitableCatalogItemType } from '@common/utils/catalogType';

export interface CatalogItemsCounterProps {
  cityId: number;
  clientType: ClientType;
  careerId: CareerId;
  sx?: SxProps;
}

const StyledBox = styled(Box)(({ theme }) => ({
  userSelect: 'none',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  backgroundColor: alpha(theme.palette.white, 0.9),
  borderRadius: '50%',
  overflow: 'hidden',
  width: 180,
  height: 180,
}));

export const CatalogItemsCounter: React.FC<CatalogItemsCounterProps> = ({ cityId, clientType, careerId, ...props }) => {
  const theme = useTheme();
  const careerDeclination = useCareerDeclination();
  const declination = useDeclination();
  const catalogType = clientTypeToSuitableCatalogItemType(clientType);

  const { data, isLoading } = coreCatalogApi.endpoints.getItemsCityStats.useQuery({ cityId });

  /**
   * Count of catalogItems in the city
   * Show minimal count if not enough items
   */
  const count = useMemo(() => {
    const MIN_COUNT = 10;
    if (!data || !data[careerId] || !data[careerId][catalogType]) {
      return MIN_COUNT;
    }
    const stats = data[careerId][catalogType] || { totalCount: MIN_COUNT };
    return stats.totalCount < MIN_COUNT ? MIN_COUNT : stats.totalCount + 9;
  }, [data, careerId, catalogType]);

  return (
    <StyledBox {...props}>
      {isLoading ? (
        <ContentSkeleton blocks={2} sx={{ width: 1 }} />
      ) : (
        <>
          <Typography component="div" sx={{ color: theme.palette['brand pink'] }} variant="h1">
            {count}
          </Typography>
          <Typography sx={{ color: theme.palette.gray }} variant="body bold">
            {catalogType === CatalogItemType.Resume ? careerDeclination(count, careerId) : declination(count, DeclinationMnemonic.Vacancy)}{' '}
            найдено
            <br />
            рядом с вами
          </Typography>
        </>
      )}
    </StyledBox>
  );
};
